import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  useCreateHeroBannerMutation,
  useUpdateHeroBannerMutation,
} from "@/store/api/homepageApi";
import FileUploadButton from "@/components/common/FileUploadButton";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";

const UPLOAD_FOLDER = "toeic/homepage/hero-banners";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required").max(200, "Max 200 characters"),
  description: Yup.string().max(1000, "Max 1000 characters").nullable(),
  teacherName: Yup.string().max(100, "Max 100 characters").nullable(),
  subjectTag: Yup.string().max(50, "Max 50 characters").nullable(),
});

const HeroBannerForm = ({ open, onClose, banner, onSuccess }) => {
  const [createHeroBanner, { isLoading: isCreating }] = useCreateHeroBannerMutation();
  const [updateHeroBanner, { isLoading: isUpdating }] = useUpdateHeroBannerMutation();

  const isEditMode = Boolean(banner);
  const isLoading = isCreating || isUpdating;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const imageFileRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      title: banner?.title || "",
      description: banner?.description || "",
      teacherName: banner?.teacherName || "",
      subjectTag: banner?.subjectTag || "",
      image: banner?.image || "",
      isActive: banner?.isActive ?? true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let imageUrl = values.image;

        if (imageFileRef.current) {
          setIsUploading(true);
          setUploadProgress(0);
          const result = await uploadToCloudinary(
            imageFileRef.current,
            UPLOAD_FOLDER,
            setUploadProgress
          );
          imageUrl = result.secure_url;
          setIsUploading(false);
          setUploadProgress(100);
        }

        const payload = {
          title: values.title,
          description: values.description || undefined,
          teacherName: values.teacherName || undefined,
          subjectTag: values.subjectTag || undefined,
          image: imageUrl || undefined,
          isActive: values.isActive,
        };

        if (isEditMode) {
          await updateHeroBanner({ id: banner._id, ...payload }).unwrap();
          toast.success("Hero banner updated successfully");
        } else {
          await createHeroBanner(payload).unwrap();
          toast.success("Hero banner created successfully");
        }
        onSuccess?.();
        handleClose();
      } catch (error) {
        setIsUploading(false);
        toast.error(error?.data?.message || "Failed to save hero banner");
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    imageFileRef.current = null;
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  const isBusy = isLoading || isUploading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEditMode ? "Edit Hero Banner" : "New Hero Banner"}
      </DialogTitle>

      {isBusy && (
        <LinearProgress
          variant={isUploading ? "determinate" : "indeterminate"}
          value={uploadProgress}
        />
      )}

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Title */}
          <TextField
            fullWidth
            label="Title *"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            size="small"
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description (tagline shown above title)"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            multiline
            rows={2}
            size="small"
            inputProps={{ maxLength: 1000 }}
            helperText={`${formik.values.description.length}/1000`}
          />

          {/* Teacher name + subject tag row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label="Teacher Name"
              name="teacherName"
              value={formik.values.teacherName}
              onChange={formik.handleChange}
              size="small"
              placeholder="e.g. Wanggicho"
              helperText="Shown in the black bar"
            />
            <TextField
              fullWidth
              label="Subject Tag"
              name="subjectTag"
              value={formik.values.subjectTag}
              onChange={formik.handleChange}
              size="small"
              placeholder="e.g. RC, LC"
              helperText="Shown in the black bar"
            />
          </Box>

          {/* Image upload */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Banner Image
            </Typography>
            {formik.values.image && (
              <Box
                component="img"
                src={formik.values.image}
                alt="Banner preview"
                sx={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: 2,
                  mb: 1,
                  display: "block",
                  border: "1px solid rgba(0,0,0,0.1)",
                }}
              />
            )}
            <FileUploadButton
              label={formik.values.image ? "Change Image" : "Upload Image"}
              accept="image/*"
              onFileSelect={({ file }) => {
                if (!file) return;
                imageFileRef.current = file;
                formik.setFieldValue("image", URL.createObjectURL(file));
              }}
            />
          </Box>

          {/* Active toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.isActive}
                onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
              />
            }
            label="Active (visible on homepage)"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
          disabled={isBusy}
          startIcon={isBusy ? <CircularProgress size={16} /> : null}
        >
          {isEditMode ? "Save Changes" : "Create Banner"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HeroBannerForm;
